import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { DocumentsService, CommonService } from '../../services/common';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-document-view',
    templateUrl: './document-view.component.html',
    styleUrls: ['./document-view.component.scss']
})
export class DocumentViewComponent implements OnInit, OnDestroy {
    documentId: string = '';
    contextId: string = '';
    documentNumber: number = 0;
    documentExtn: string = ''; // Document extension passed via URL
    fileCategory: string = 'image'; // 'pdf', 'video', 'audio', 'image', 'doc', 'cad', 'other'
    isLoading: boolean = false;
    picData: any = null;
    showPicViewer: boolean = false;
    errorMessage: string = '';
    noAccess: boolean = false;

    private destroy$ = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private location: Location,
        private sanitizer: DomSanitizer,
        private documentsService: DocumentsService,
        public commonService: CommonService,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        // Get document ID and context ID (e.g., projectId) from route parameters
        this.route.paramMap.pipe(
            takeUntil(this.destroy$)
        ).subscribe(params => {
            this.documentId = params.get('documentId') || '';
            this.contextId = params.get('contextId') || '';

            // Get documentNumber and documentExtn from query params if available
            this.route.queryParamMap.pipe(
                takeUntil(this.destroy$)
            ).subscribe(queryParams => {
                this.documentNumber = parseInt(queryParams.get('documentNumber') || '0');
                this.documentExtn = queryParams.get('documentExtn') || '';

                // Check access based on context (project or exploration)
                if (this.contextId) {
                    const currentPath = this.location.path().toLowerCase();
                    if (currentPath.includes('/project/') || currentPath.includes('/projects/')) {
                        if (!this.commonService.hasProjectAccess(this.contextId)) {
                            this.noAccess = true;
                            this.errorMessage = 'You do not have access to this document';
                            return;
                        }
                    } else if (currentPath.includes('/exploration/') || currentPath.includes('/explorations/')) {
                        if (!this.commonService.hasExplorationAccess(this.contextId)) {
                            this.noAccess = true;
                            this.errorMessage = 'You do not have access to this document';
                            return;
                        }
                    }
                }

                if (this.documentId && this.contextId) {
                    this.loadDocument();
                } else if (this.documentId && !this.contextId) {
                    // Allow document viewing without contextId for future use
                    this.loadDocument();
                } else {
                    this.errorMessage = 'Invalid document or context ID';
                    this.toastr.error('Invalid document or context ID', 'Error');
                }
            });
        });
    }

    /**
     * Load document from API
     */
    private loadDocument(): void {
        this.isLoading = true;

        // Convert documentId to number for the API call
        const documentIdNumber = parseInt(this.documentId, 10);

        // Determine if this is a dump document (from query param or other logic)
        const isDump = this.route.snapshot.queryParamMap.get('docType') === 'dump';

        // Fetch document bytes with sourceType if dump
        this.documentsService.getDocumentBytesById(documentIdNumber, this.documentNumber, isDump ? 'dump' : undefined).subscribe(
            async (response: any) => {
                try {
                    // Extract base64 from response
                    const base64Data = response?.result || response;

                    // Get file category using centralized method
                    this.fileCategory = this.commonService.getFileCategoryByExtension(this.documentExtn);

                    // Create Object URL using efficient fetch method
                    const mimeType = this.commonService.getMimeTypeFromExtension(this.documentExtn) || 'application/octet-stream';
                    const picFile = await this.documentsService.convertBase64ToObjectURL(base64Data, mimeType);

                    let safePicFile: any = picFile;
                    if (['pdf', 'video', 'audio'].includes(this.fileCategory)) {
                        safePicFile = this.sanitizer.bypassSecurityTrustResourceUrl(picFile);
                    }

                    // Set up pic viewer data 
                    this.picData = {
                        picTitle: `Document View - ${this.documentId}`,
                        picFile: safePicFile,
                        fileCategory: this.fileCategory,
                        documentName: `${this.documentId}${this.documentExtn ? '.' + this.documentExtn.toLowerCase() : ''}`,
                        // Store raw base64 for download purposes
                        base64Data: base64Data,
                        docType: isDump ? 'dump' : 'document'
                    };

                    this.showPicViewer = true;
                    this.isLoading = false;
                } catch (error) {
                    this.errorMessage = 'Failed to process document';
                    this.toastr.error('Failed to process document', 'Error');
                    this.isLoading = false;
                }
            },
            (error: any) => {
                console.error('Failed to fetch document bytes:', error);
                this.errorMessage = 'Failed to load document';
                this.toastr.error('Failed to load document', 'Error');
                this.isLoading = false;
            }
        );
    }

    downloadDocument() {
        this.documentsService.downloadFileFromBase64(this.picData.base64Data, this.picData.documentName);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
